'use client';

import {
  EllipsisHorizontalIcon,
  EyeIcon,
  EyeSlashIcon,
  PencilIcon,
  TrashIcon,
} from '@heroicons/react/16/solid';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { Button } from '../ui/button';
import type { Bookmark, Folder } from '@/lib/bookmark/types';
import { useWorkflows } from '@/providers/workflow-provider';
import { usePerms } from '@/hooks/usePerms';
import { ShareIcon } from 'lucide-react';
import { toast } from 'sonner';
import { editById } from '@/lib/bookmark/utils/bookmark-utils';
import { useMemo } from 'react';
import { cn } from '@/lib/cn';

interface HandleWorkflowDropdownProps {
  readonly isEditMode: boolean;
  readonly type: 'folder' | 'bookmark';
  readonly currentItem: Folder | Bookmark;
  readonly workEngagementId: string | null;
}

export function HandleWorkflowDropdown({
  isEditMode,
  type,
  currentItem,
  workEngagementId,
}: HandleWorkflowDropdownProps) {
  const { canView: canViewOrganizationUser } = usePerms('organizationuser');

  const { modals, userWorkflows, workEngagementWorkflows, save } =
    useWorkflows();

  // When rendering for a specific work engagement, use that workflow
  // Otherwise use user workflows
  const workflows = useMemo(() => {
    if (workEngagementId) {
      return workEngagementWorkflows?.find(
        (w) => w.work_engagement_id === workEngagementId
      );
    }
    return userWorkflows;
  }, [workEngagementId, workEngagementWorkflows, userWorkflows]);

  const translatedType = type === 'folder' ? 'arbetsflöde' : 'vy';
  const folders = workflows?.folders;

  const onHandleEditItem = (
    editedItem: Partial<Folder | Bookmark>
  ) => {
    const clientId = editedItem.client_id || modals.edit.state?.item.client_id;

    if (!clientId || !folders || folders.length === 0 || !workflows) {
      console.error('Cannot edit item: missing client_id or folders');
      toast.error(`Misslyckades med att uppdatera ${translatedType}`);
      return;
    }

    const updatedFolders = editById(folders, clientId, editedItem);

    const savePromise = save({
      ...workflows,
      folders: updatedFolders,
    });

    toast.promise(savePromise, {
      loading: `Uppdaterar ${translatedType}...`,
      success: `Uppdatering av ${translatedType} lyckades`,
      error: `Misslyckades med att uppdatera ${translatedType}`,
    });
  };

  if (isEditMode) {
    return (
      <div
        className={cn(
          'flex items-center',
          type === 'bookmark' ? 'pr-2' : 'pr-4'
        )}
      >
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant='ghost' className='size-8 p-0'>
              <span className='sr-only'>Öppna meny</span>
              <EllipsisHorizontalIcon className='size-4' />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className='bg-background'>
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                modals.edit.open(currentItem, type, workEngagementId);
              }}
            >
              <PencilIcon className='mr-1 size-4' />
              Redigera
            </DropdownMenuItem>
            {type === 'folder' && (
              <DropdownMenuItem
                disabled={!canViewOrganizationUser}
                onClick={(e) => {
                  e.stopPropagation();
                  modals.share.open(currentItem, type, workEngagementId);
                }}
              >
                <ShareIcon className='mr-2 w-4' /> Dela
              </DropdownMenuItem>
            )}
            {type === 'folder' && (
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  if (!currentItem) return;

                  onHandleEditItem({
                    client_id: currentItem.client_id,
                    pinned: !currentItem.pinned,
                  });
                }}
              >
                {currentItem.pinned ? (
                  <>
                    <EyeSlashIcon className='mr-2 w-4' /> Dölj från "Min
                    arbetsdag"
                  </>
                ) : (
                  <>
                    <EyeIcon className='mr-2 w-4' /> Visa på "Min arbetsdag"
                  </>
                )}
              </DropdownMenuItem>
            )}
            <DropdownMenuItem
              onClick={(e) => {
                e.stopPropagation();
                modals.delete.open(currentItem, type, workEngagementId);
              }}
            >
              <TrashIcon className='mr-1 size-4' />
              Radera
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    );
  }

  return null;
}
